/// <reference types="vite-plugin-svgr/client" />

import { Avatar, Button, Divider, Input, Layout } from 'antd';
import Sider from 'antd/lib/layout/Sider';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './BaseIndex.scss';

import { CompassOutlined, LockOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { analytics, useAnalytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { useSessionTracking } from '../../analytics/useSessionTracking';
import { getOrganization, Organization, UserOrganizationAccess } from '../../api/organization';
import { DiscoverableWorkspace, getUserWorkspaces } from '../../api/workspace';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import { LogoIcon } from '../../assets/CustomIcons/LogoIcon';
import ChevronLeftSvg from '../../assets/icons/chevron-left.svg?react';
import {
  AccessTokenUser,
  getCurrentlyActiveOrganization,
  getUsersAccessibleOrganizations,
  OrganizationAccessRoles,
  setCurrentlyActiveOrganization,
  useGetUserFromAccessToken,
} from '../../common/auth';
import MenuItemNewSpace from '../../components/MenuItemNewSpace/MenuItemNewSpace';
import MenuSideBarBottom from '../../components/MenuSideBarBottom/MenuSideBarBottom';
import SelfSortBanner from '../../components/SelfSort/SelfSortBanner';
import { BaseButton } from '../../components/UI/BaseButton';
import { getOrgCreatorUserId, useSelfSort } from '../../hooks/useSelfSort';
import { useNavigateToTask } from '../../hooks/useTaskNavigation';
import { InboxSidebarPanel } from '../../components/Notifications/InboxSidebarPanel/InboxSidebarPanel';
import SideBarItem from '../../components/UI/Components/SideBarItem';
import SpaceBarItem from '../../components/UI/Components/SpaceBarItem';
import { headerHeight, sidebarWidth, sidebarWidthCollapsed } from '../../constants/styles/mixins';
import { useUserOrganizationSocket } from '../../hooks/useUserOrganizationSocket';
import RoutePaths from '../../router/RoutePaths';
import {
  useGetOrganizationAccessesWithOrganization,
  useUnresolvedNotificationsCount,
} from '../../serverState/organization';
import {
  useGetDiscoverableWorkspaces,
  useGetWorkspace,
  useGetWorkspaces,
  useJoinWorkspaceMutation,
} from '../../serverState/workspace';
import Workspace from '../../types/Workspace';
import { chooseNoSpaceDestination, getSpaceDefaultRoute, resolveOrgFromUrl } from '../../utils/workspaceUtils';
import { WORKSPACE_VISIBILITY } from '../Onboarding/WorkspaceOnboarding';
import { ModalContext, ModalType } from '../../context/ModalContext';
import { SprintStatusType } from '../../components/UI/SprintStatusLabel';
import { LayoutContext } from './context/LayoutContext';
import { getSideBarItemGroups, SideBarItemEnum } from './helpers/SideBarItems';
import { sessionOrganizationId } from './helpers/sessionOrganization';
import { useOrgRoleInfo } from './useOrgRoleInfo';

// Modules that get a hover "+" CTA in the sidebar for quick task creation (DEV-1446).
const CREATABLE_SIDEBAR_ITEMS: Partial<Record<SideBarItemEnum, SprintStatusType>> = {
  [SideBarItemEnum.triage]: 'triage',
  [SideBarItemEnum.backlog]: 'none',
  [SideBarItemEnum.planning]: 'planning',
  [SideBarItemEnum.sprint]: 'sprint',
};

export default function BaseIndex() {
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalContext);
  const [activeOrganization, setActiveOrganization] = useState<Organization>();
  const [collapsed, setCollapsed] = useState(false);

  const [spaces, setSpaces] = useState<Workspace[]>([]);
  const [selectedSpace, setSelectedSpace] = useState<Workspace | undefined>(undefined);
  const [isSpacesOpened, setIsSpacesOpened] = useState(false);
  const [spacesSearchValue, setSpacesSearchValue] = useState('');
  const spacesSearchInputRef = useRef<any>(null);

  const [user, setUser] = useState<AccessTokenUser | undefined>(undefined);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  // The opened notification, kept with the task it routed to so the green "selected"
  // outline can be dropped again once that task view is left (#3004).
  const [selectedNotification, setSelectedNotification] = useState<{ id: number; taskId?: number }>();

  const workspacesQuery = useGetWorkspaces(Number(activeOrganization?.id));
  const workspaceQuery = useGetWorkspace();
  const discoverableQuery = useGetDiscoverableWorkspaces(Number(activeOrganization?.id));
  const joinWorkspaceMutation = useJoinWorkspaceMutation();
  const organizationsQuery = useGetOrganizationAccessesWithOrganization();
  const organizations = organizationsQuery.data;
  const navigateToTask = useNavigateToTask();

  const { organizationRole } = useOrgRoleInfo();

  const filteredSpaces = useMemo(() => {
    return spaces.filter((space) => space.name.toLowerCase().includes(spacesSearchValue.toLowerCase()));
  }, [spaces, spacesSearchValue]);

  const filteredDiscoverableSpaces = useMemo(() => {
    return (discoverableQuery.data || []).filter((space) =>
      space.name.toLowerCase().includes(spacesSearchValue.toLowerCase())
    );
  }, [discoverableQuery.data, spacesSearchValue]);

  const handleJoinSpace = async (space: DiscoverableWorkspace) => {
    const organizationId = activeOrganization?.id;
    if (!organizationId) return;

    const joinedSpace = await joinWorkspaceMutation.mutateAsync({ spaceId: space.id, organizationId });

    analytics.track(AnalyticsEvent.spaceJoined, { spaceId: space.id, organizationId });

    const route = getSpaceDefaultRoute(activeOrganization?.publicId, joinedSpace);
    if (route) navigate(route);
    setSelectedSpace(joinedSpace);
    setIsSpacesOpened(false);
  };

  useAnalytics();

  useEffect(() => {
    getCurrentlyActiveOrganization()
      .then(async (org) => {
        const organization = (await getOrganization(org.id)).data as Organization;
        setActiveOrganization({ ...org, ...organization });
        await setCurrentlyActiveOrganization({ ...org, ...organization });
        fetchUsersWorkspaces(org.id).catch((e) => console.log(e));
      })
      .catch((e) => {
        navigate(RoutePaths.LoginSelectOrganization);
        console.log(e);
      });
  }, []);

  const currentUser = useGetUserFromAccessToken();

  // Only the org CREATOR (not every admin) is asked for the org's firmographics.
  // The creator is the earliest OrganizationAccess for the active org, read from
  // the /access list. undefined until both the user and the org's accesses load,
  // so the banner stays hidden rather than briefly mis-resolving (#2659).
  const currentUserId = currentUser.data?.user.id;
  const isOrgOwner = useMemo<boolean | undefined>(() => {
    if (currentUserId === undefined || !activeOrganization) return undefined;
    const orgs = organizations as UserOrganizationAccess[] | undefined;
    const activeAccess = orgs?.find((org) => org.id === activeOrganization.id)?.OrganizationAccess;
    const creatorUserId = getOrgCreatorUserId(activeAccess);
    if (creatorUserId === undefined) return undefined;
    return creatorUserId === currentUserId;
  }, [organizations, activeOrganization, currentUserId]);

  // Resolves the self-sort variant that feeds the persistent sidebar banner.
  // The modal is opened only from the banner — it never auto-opens. Reads
  // companySize from the LIVE org (activeOrganization), not the localforage
  // snapshot, so the owner/member split is correct for brand-new orgs.
  const { variant: selfSortVariant } = useSelfSort({
    isOrgOwner,
    organization: activeOrganization,
    selfSortCompletedAt: currentUser.data?.user.selfSortCompletedAt,
  });

  // activeOrganization is set only when the org id changes (on switch), so the
  // self-sort submit's optimistic firmographics patch to usersOrganizationAccess
  // wouldn't reach it and the owner banner would linger after saving. Mirror the
  // matching org's firmographics into activeOrganization so the banner hides once
  // companySize is saved for the current org.
  useEffect(() => {
    if (!activeOrganization) return;
    const orgs = organizations as Organization[] | undefined;
    const fresh = orgs?.find((org) => org.id === activeOrganization.id);
    if (fresh && fresh.companySize !== activeOrganization.companySize) {
      setActiveOrganization((prev) => (prev ? { ...prev, ...fresh } : prev));
    }
  }, [organizations, activeOrganization]);

  useEffect(() => {
    if (currentUser.data) {
      setUser(currentUser.data);

      // analytics identify
      analytics.identify(currentUser.data.user.id.toString());
      getUsersAccessibleOrganizations().then((orgs) =>
        analytics.setUserProperties({
          organizationIds: orgs.map((orgAccess) => orgAccess.id),
        })
      );
    }
  }, [currentUser.data]);

  // Declared after the identify effect so app.session.started (load) is sent for the identified user.
  // On a deep link, the org the URL names, once BaseIndex has switched to it.
  useSessionTracking({
    userId: currentUser.data?.user.id,
    organizationId: sessionOrganizationId(location.pathname, activeOrganization),
  });

  useEffect(() => {
    const workspaces = workspacesQuery.data;

    if (!workspaces) {
      return;
    }
    setSpaces(workspaces);

    const urlSelection = location.pathname.split('/');
    if (
      location.pathname === '/' ||
      (urlSelection.length > 2 && urlSelection[2] === 'space' && urlSelection[1] === 'my')
    ) {
      const space = workspaces[0];
      if (space) {
        setSelectedSpace(space);
        const route = getSpaceDefaultRoute(activeOrganization?.publicId, space);
        if (route) navigate(route);
      } else {
        const dest = chooseNoSpaceDestination({
          discoverableSettled: discoverableQuery.isSuccess || discoverableQuery.isError,
          discoverableCount: discoverableQuery.data?.length ?? 0,
        });
        if (dest === 'wait') return; // re-runs when the discoverable query settles
        navigate(`/${activeOrganization?.publicId}/${dest}`);
      }
    } else if (urlSelection.length > 2 && urlSelection[2] === 'space') {
      const resolution = resolveOrgFromUrl(
        urlSelection[1],
        organizations as UserOrganizationAccess[] | undefined,
        organizationsQuery.isFetched
      );
      if (resolution.status === 'wait') return; // re-runs when the access list settles
      if (resolution.status === 'no-access') {
        navigate(RoutePaths.LoginSelectOrganization);
        return;
      }
      const organization = resolution.organization;
      if (activeOrganization?.id !== organization.id) {
        void setCurrentlyActiveOrganization(organization);
        setActiveOrganization(organization);
      }
      const space = workspaces.find((w) => w.publicId === urlSelection[3]);
      if (space) {
        setSelectedSpace(space);
      }
    } else if (urlSelection[2] === 'inbox' || urlSelection[2] === 'decisions' || urlSelection[2] === 'all-tasks') {
      // Org-level routes (Decisions, All Tasks) — don't redirect to a space
      const organization = organizations?.find((o) => o.publicId === urlSelection[1]);
      if (organization) {
        if (activeOrganization?.id !== organization.id) {
          void setCurrentlyActiveOrganization(organization);
          setActiveOrganization(organization);
        }
      }
      // Select first space for sidebar context, but stay on the org-level route
      if (workspaces.length > 0 && !selectedSpace) {
        setSelectedSpace(workspaces[0]);
      }
    } else {
      navigate(RoutePaths.Home);
      window.location.reload();
    }
  }, [
    workspacesQuery.data,
    discoverableQuery.data,
    discoverableQuery.status,
    organizations,
    organizationsQuery.isFetched,
  ]);

  useEffect(() => {
    if (workspaceQuery.data && workspaceQuery.data.id === selectedSpace?.id) {
      setSelectedSpace(workspaceQuery.data);
    }
  }, [workspaceQuery.data]);

  useEffect(() => {
    if (isSpacesOpened && spacesSearchInputRef.current) {
      spacesSearchInputRef.current.focus();
    }
  }, [isSpacesOpened]);

  // There is a reason I wanted async here, but I forgot it
  // eslint-disable-next-line @typescript-eslint/require-await
  const fetchUsersWorkspaces = async (orgId: number) => {
    getUserWorkspaces(orgId)
      .then((res) => {
        setSpaces(res.data);
      })
      .catch((e) => console.log(e));
  };

  // User organization socket
  useUserOrganizationSocket(activeOrganization?.id as number);

  const notificationsCount = useUnresolvedNotificationsCount(activeOrganization?.id as number);

  // The outline marks "this is the notification you are looking at", so it lives
  // exactly as long as its task view does — routing anywhere else drops it (#3004).
  useEffect(() => {
    if (!selectedNotification?.taskId) return;
    const openTaskId = location.pathname.match(/\/task\/(\d+)/)?.[1];
    if (openTaskId !== String(selectedNotification.taskId)) setSelectedNotification(undefined);
  }, [location.pathname, selectedNotification]);

  const sidebarItemGroups = selectedSpace ? getSideBarItemGroups(selectedSpace.framework) : [];

  return (
    <div className="overflow-hidden overscroll-none fixed inset-0">
      <Layout hasSider={true} className="h-screen">
        <Sider
          className="group [&_.ant-layout-sider-children_>*]:px-4 [&_.ant-divider]:p-0 "
          style={{
            borderRight: '1px solid var(--color-neutral-300)',
            zIndex: 1,
          }}
          width={sidebarWidth}
          theme="light"
          collapsible={true}
          collapsed={collapsed}
          collapsedWidth={sidebarWidthCollapsed}
          trigger={null}
        >
          <div
            style={{ height: headerHeight }}
            className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}
          >
            {!collapsed && (
              <LogoFullIcon
                onClick={() => {
                  const space = spaces?.[0];
                  if (space) {
                    setSelectedSpace(space);
                    const route = getSpaceDefaultRoute(activeOrganization?.publicId, space);
                    if (route) navigate(route);
                  }
                }}
              />
            )}
            {collapsed && <LogoIcon className="group-hover:hidden" />}
            <Button
              size="small"
              shape="default"
              className="expand-sidebar-button hidden group-hover:flex  items-center justify-center"
              icon={
                collapsed ? (
                  <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="pb-1" />
                ) : (
                  <ChevronLeftSvg width={16} className="pt-1" />
                )
              }
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
          {!isSpacesOpened && !isInboxOpen && (
            <div className="flex justify-between flex-col" style={{ height: `calc(100% - ${headerHeight + 20}px)` }}>
              <div className="flex flex-col gap-2">
                {selectedSpace && (
                  <SideBarItem
                    iconLeft={
                      <Avatar
                        className={`flex-shrink-0 text-neutral-900 transition-colors duration-300 ${collapsed ? 'bg-white' : 'bg-brand-grey-light'}`}
                        shape="square"
                        size={24}
                      >
                        {selectedSpace?.name.substring(0, 1)}
                      </Avatar>
                    }
                    iconRight={<ChevronLeftSvg className="w-5 h-5 rotate-180" />}
                    text={selectedSpace?.name || ''}
                    selected={false}
                    collapsed={collapsed}
                    onClick={() => setIsSpacesOpened(true)}
                  />
                )}

                <div className={`flex flex-col gap-1 rounded-lg bg-brand-white ${collapsed ? 'py-3 px-1.5' : 'p-3'}`}>
                  {sidebarItemGroups.map((item, index) => (
                    <Fragment key={item.key}>
                      {(index === 1 || item.key === SideBarItemEnum.analytics) && (
                        <div className="my-1 h-px bg-neutral-300 flex-shrink-0" />
                      )}
                      <SpaceBarItem
                        iconLeft={item.icon}
                        text={item.label}
                        badge={item.badge}
                        selected={location.pathname.includes(item.key)}
                        collapsed={collapsed}
                        onClick={() =>
                          navigate(`/${activeOrganization?.publicId}/space/${selectedSpace?.publicId}/${item.key}`)
                        }
                        onCreateClick={
                          CREATABLE_SIDEBAR_ITEMS[item.key]
                            ? () => openModal(ModalType.CreateItem, { sprintStatus: CREATABLE_SIDEBAR_ITEMS[item.key] })
                            : undefined
                        }
                      />
                    </Fragment>
                  ))}
                </div>

                <SelfSortBanner variant={selfSortVariant} collapsed={collapsed} />
              </div>

              <MenuSideBarBottom
                activeOrganization={activeOrganization}
                collapsed={collapsed}
                notificationsCount={notificationsCount}
                user={user}
                organizationRole={organizationRole as OrganizationAccessRoles}
                inboxOpen={isInboxOpen}
                onToggleInbox={() => {
                  // Closing or reopening the Inbox is a fresh start: nothing is selected (#3004).
                  setSelectedNotification(undefined);
                  setIsInboxOpen((v) => !v);
                }}
              />
            </div>
          )}
          {isSpacesOpened && (
            <div className="flex flex-col gap-4" style={{ height: `calc(100% - ${headerHeight + 20}px)` }}>
              {/* Fixed header section */}
              <div className="flex-shrink-0">
                <div className="flex gap-1 ml-4 cursor-pointer" onClick={() => setIsSpacesOpened(false)}>
                  <ChevronLeftSvg width={16} className="text-neutral-900" />
                  {!collapsed && <span className="text-neutral-900 text-sm font-medium">Back</span>}
                </div>
              </div>

              <div className="flex-shrink-0">
                <div className="flex gap-2 ml-4">
                  <TeamOutlined width={16} className="text-neutral-900" />
                  {!collapsed && <span className="text-brand-grey-dark text-base font-semibold">Teamspaces</span>}
                </div>
              </div>

              <Divider className="m-0 flex-shrink-0" />

              <div className="flex-shrink-0">
                <Input
                  ref={spacesSearchInputRef}
                  placeholder="Search for Spaces"
                  prefix={<SearchOutlined className="mr-1" />}
                  allowClear={spacesSearchValue.length > 0 ? true : false}
                  className="[&_input::placeholder]:!text-neutral-900 px-3 text-neutral-900"
                  value={spacesSearchValue}
                  onChange={(e) => setSpacesSearchValue(e.target.value)}
                />
              </div>

              {filteredSpaces.length === 0 && filteredDiscoverableSpaces.length === 0 && (
                <div className="ml-2 text-neutral-900 text-xs font-semibold leading-none flex-shrink-0">
                  No Spaces found.
                </div>
              )}

              {/* Own spaces and discoverable public spaces live in separate cards, per design. */}
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
                <div
                  className={`flex-shrink-0 flex flex-col gap-1 rounded-lg bg-brand-white ${collapsed ? 'py-3 px-1.5' : 'p-3'}`}
                >
                  {!collapsed && (
                    <div className="flex-shrink-0 mb-1 px-1 flex items-center gap-1.5 text-neutral-900 text-xs font-semibold uppercase">
                      <TeamOutlined />
                      My Teamspaces
                    </div>
                  )}

                  {filteredSpaces.map((space) => (
                    <div key={space.id} className="flex-shrink-0">
                      <SpaceBarItem
                        iconLeft={
                          <Avatar
                            className={`flex-shrink-0 text-neutral-900 transition-colors duration-300 ${collapsed ? (selectedSpace?.id === space.id ? 'bg-taskonioGreen-100' : 'bg-white hover:bg-neutral-200') : 'bg-brand-grey-light'}`}
                            shape="square"
                            size={24}
                          >
                            {space.name.substring(0, 1)}
                          </Avatar>
                        }
                        iconRight={
                          space.visibility === WORKSPACE_VISIBILITY.Private ? (
                            <LockOutlined className="text-neutral-900" />
                          ) : undefined
                        }
                        text={space.name}
                        selected={selectedSpace?.id === space.id}
                        collapsed={collapsed}
                        onClick={() => {
                          const route = getSpaceDefaultRoute(activeOrganization?.publicId, space);
                          if (route) navigate(route);
                          setSelectedSpace(space);
                          setIsSpacesOpened(false);
                        }}
                      />
                    </div>
                  ))}

                  <div className="flex-shrink-0">
                    <MenuItemNewSpace
                      collapsed={collapsed}
                      onClick={() => navigate(`/${activeOrganization?.publicId}/new-space`)}
                    />
                  </div>
                </div>

                {filteredDiscoverableSpaces.length > 0 && (
                  <div
                    className={`flex-shrink-0 flex flex-col gap-1 rounded-lg bg-brand-white ${collapsed ? 'py-3 px-1.5' : 'p-3'}`}
                  >
                    {!collapsed && (
                      <div className="flex-shrink-0 mb-1 px-1 flex items-center gap-1.5 text-neutral-900 text-xs font-semibold uppercase">
                        <CompassOutlined />
                        Public Spaces
                      </div>
                    )}
                    {filteredDiscoverableSpaces.map((space) => (
                      <div
                        key={`discover-${space.id}`}
                        className="group/discover flex-shrink-0 flex items-center gap-2 px-1 py-1 rounded hover:bg-neutral-200"
                      >
                        <Avatar className="flex-shrink-0 text-neutral-900 bg-brand-grey-light" shape="square" size={24}>
                          {space.name.substring(0, 1)}
                        </Avatar>
                        {!collapsed && (
                          <>
                            <span className="flex-1 min-w-0 truncate text-brand-grey-dark text-sm">{space.name}</span>
                            <BaseButton
                              type="secondary"
                              size="small"
                              className="hidden group-hover/discover:inline-flex"
                              disabled={joinWorkspaceMutation.isPending}
                              onClick={() => {
                                void handleJoinSpace(space);
                              }}
                            >
                              Join
                            </BaseButton>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {isInboxOpen && activeOrganization && (
            // Cancel the Sider's forced `.ant-layout-sider-children > * { px-4 }` so the
            // inbox spans the full width; insets are re-applied inside InboxSidebarPanel.
            <div className="flex flex-col !px-0" style={{ height: `calc(100% - ${headerHeight + 20}px)` }}>
              <InboxSidebarPanel
                organizationId={activeOrganization.id}
                spaceId={selectedSpace?.id}
                collapsed={collapsed}
                selectedNotificationId={selectedNotification?.id}
                onBack={() => {
                  setSelectedNotification(undefined);
                  setIsInboxOpen(false);
                }}
                onOpenNotification={(n) => {
                  setSelectedNotification({ id: n.id, taskId: n.Task?.taskId });
                  const space = spaces.find((s) => s.id === n.Workspace?.id);
                  if (space) setSelectedSpace(space);
                  if (n.Task && n.Workspace) {
                    navigateToTask({ workspaceId: n.Workspace.publicId, taskId: n.Task.taskId });
                  } else if (space) {
                    // Space notifications (invited_to_space, user_added_to_space) carry a
                    // Workspace but no Task, so there's nothing to navigateToTask with (#3032).
                    const route = getSpaceDefaultRoute(activeOrganization?.publicId, space);
                    if (route) navigate(route);
                  }
                }}
              />
            </div>
          )}
        </Sider>
        <Layout className="site-layout bg-white">
          {/* Content of the pages. Rest is navigation */}
          <LayoutContext.Provider value={{ collapsed, selectedSpace }}>
            <Outlet />
          </LayoutContext.Provider>
        </Layout>
      </Layout>
    </div>
  );
}
